import { sortListOfObjectsByDateField, useApiClient, useFetch } from '@marageti/z4-lib';
import React, { useMemo, useState } from 'react';
import { FilterOp, SearchRequest, SearchResult } from '@marageti/z4-sdk/lib/entity';
import { PartnerAvailability } from '@marageti/z4-sdk/lib/travel';
import { add, format } from 'date-fns';
import PartnerCapacityDialogView, { DialogState } from './partner-capacity-dialog-view';

type PartnerCapacityDialogContainerProps = {
  partnerId: string;
  currentCapacity: number;
  adjustedCapacity: number;
  children: (props: { handleOpen: () => void }) => React.ReactNode;
};

const PartnerCapacityDialogContainer = ({
  children,
  partnerId,
  currentCapacity,
  adjustedCapacity,
}: PartnerCapacityDialogContainerProps) => {
  const [dialogState, setDialogState] = useState<DialogState>('closed');
  const apiClient = useApiClient();
  const today = new Date();
  const twelveMonthsFromToday = add(today, { months: 12 });
  const todayString = format(today, 'yyyy-MM-dd');
  const twelveMonthsFromTodayString = format(twelveMonthsFromToday, 'yyyy-MM-dd');

  const searchRequest: SearchRequest = {
    filterBy: [{
      field: 'endDate',
      op: FilterOp.FilterOpGTE,
      value: [todayString],
    },
    {
      field: 'startDate',
      op: FilterOp.FilterOpLTE,
      value: [twelveMonthsFromTodayString],
    },
    {
      field: 'partnerId',
      op: FilterOp.FilterOpTerm,
      value: [partnerId!],
    },
    ],
    sortBy: [{ field: 'startDate', order: 'asc' }],
    pagination: { limit: 10000, page: 1 },
  };

  const {
    data: partnerAvailabilitiesData,
    isLoading,
    error: availabilitiesError,
  } = useFetch<SearchResult<PartnerAvailability>>(
    apiClient.partnersClient.postPartnerAvailabilitySearch,
    [searchRequest],
    partnerId !== undefined && dialogState !== 'closed',
  );

  const partnerAvailability = useMemo(() => {
    if (partnerAvailabilitiesData && partnerAvailabilitiesData.hits.length > 0) {
      return sortListOfObjectsByDateField(partnerAvailabilitiesData.hits || [], 'startDate');
    }
    return undefined;
  }, [partnerAvailabilitiesData]);

  const handleOpen = () => setDialogState('opening');
  const handleClose = () => setDialogState('closing');

  return (
    <PartnerCapacityDialogView
      dialogState={dialogState}
      setDialogState={setDialogState}
      handleOpen={handleOpen}
      handleClose={handleClose}
      currentCapacity={currentCapacity}
      adjustedCapacity={adjustedCapacity}
      partnerAvailability={partnerAvailability}
      isLoading={isLoading && dialogState === 'opening'}
      hasError={availabilitiesError !== undefined}
    >
      {children}
    </PartnerCapacityDialogView>
  );
};

export default PartnerCapacityDialogContainer;
